/**
 * NL/EN — Phase 3 step 5's language half.
 *
 * WHY NOT AN i18n LIBRARY, and the precedent is `platform/router.ts` two files over:
 * this app has a 500 kB bundle ceiling that the smoke test enforces, and
 * `react-i18next` plus its ICU formatter costs more than the entire scan fallback.
 * What is actually needed here is a keyed lookup, an interpolation, a stored
 * preference and a switcher. That is this file. **If the app ever needs plurals in
 * six languages, replace this deliberately — do not grow it into a library.**
 *
 * A MISSING TRANSLATION IS A TYPE ERROR, NOT A RUNTIME FALLBACK. `EN` is the source
 * of truth and `TranslationKey` is derived from it, so `NL` is declared as
 * `Record<TranslationKey, string>` — leave a key out and `tsc` fails. The usual
 * library behaviour, falling back to the key or to English at runtime, would put the
 * discovery in front of a Dutch-speaking user in the middle of a shoot instead of in
 * front of the person who can fix it. Same instinct as Guardrail 10.
 *
 * THE DUTCH IS THE ATELIER'S OWN VOCABULARY where the domain has one — *uitgeleend*
 * for checked out, *retour* for a return, *boeking* for a booking, *uitlener* for a
 * lender — rather than a literal translation of the English. **It has not been read
 * by a native speaker yet**; that review is on the switch-day checklist and the
 * admin guide names it.
 */

import { safeGet, safeSet } from '@mononoke/shared';
import { localStore } from './browser.js';

/**
 * Persistence goes through `browser.ts`'s already-probed handle rather than touching
 * `localStorage` directly — the house lint rule, and it is right: Safari in private
 * mode throws on ACCESS, not just on write, and a language preference is never worth
 * a crash. `localStore` is null where storage is unavailable and everything below
 * degrades to "detect from the browser each time", which is the correct behaviour.
 */
function readSetting(key: string): string | null {
  return localStore ? safeGet(localStore, key) : null;
}

function writeSetting(key: string, value: string): void {
  if (localStore) safeSet(localStore, key, value);
}

export type Language = 'nl' | 'en';

/**
 * The English source of truth. Keys are `screen.thing`, which keeps them greppable
 * from the screen that uses them — the practical property when a string needs
 * changing and nobody remembers what it was called.
 */
export const EN = {
  // Navigation and shared verbs, used from several screens.
  'nav.items': '← Items',
  // The persistent nav's Items tab — distinct from `nav.items`, which ~50 e2e call
  // sites know as the '← Items' back button and which therefore keeps its arrow.
  'nav.boxes': 'Boxes',
  'nav.retour': 'Retour',
  'nav.itemsTab': 'Items',
  'nav.more': 'More',
  /* The sidebar's two group headings (redesign). ⚠️ English in BOTH dictionaries —
     Dutch is on indefinite hold and nothing new gets translated. */
  'nav.groupGoods': 'The goods',
  'nav.groupDesk': 'The desk',
  'nav.closeMenu': 'Close menu',
  'nav.dashboard': 'Dashboard',
  'nav.import': 'Import',

  // The import screen (admin). The counts are deliberately plain: an import that
  // says "done" without saying how many, and how many it skipped, is a claim.
  'import.heading': 'Import a catalogue',
  // The "nothing is overwritten" half moved OUT to a level-3 info band (redesign 7c) — the
  // safety rule is the reason it is safe to press, so it reads as a band, not a tail clause.
  'import.intro': 'Reads a CSV export from another system and ADDS what is missing. Administrators only.',
  'import.file': 'The CSV file',
  'import.check': 'Check the file',
  'import.checking': 'Checking…',
  'import.checkHint': 'Nothing is written yet — this shows you what would happen.',
  'import.previewHeading': 'What this import would do',
  'import.doneHeading': 'Imported',
  'import.rowsRead': 'Rows read',
  'import.itemsToAdd': 'Items to add',
  'import.itemsAdded': 'Items added',
  'import.skippedExisting': 'Already here, skipped',
  'import.newCategories': 'New categories',
  'import.newLocations': 'New locations',
  'import.newFields': 'New fields',
  'import.custodyRows': 'Items arriving in someone’s custody',
  'import.photosToLink': 'Photos linked',
  'import.codesSkipped': 'Tag codes NOT taken',
  'import.codesSkippedHint': 'Another item already carries these labels, so they stay where they are. The item still imports.',
  'import.rejects': 'Values not imported',
  'import.rejectsHint': 'Each one is named rather than guessed at. The row still imports; the value does not.',
  'import.unmapped': 'Columns nothing imports',
  'import.unmappedHint': 'Present in the file and not mapped by this profile. Nothing here is dropped silently.',
  'import.row': 'row',
  'import.rowsWord': 'rows',
  'import.andMore': '…and {count} more.',
  'import.confirm': 'Import {count} items',
  'import.importing': 'Importing…',
  'import.nothingToAdd': 'Everything in this file is already here.',
  'import.recognisedAs': 'Recognised as {profile}',
  'import.strays': 'Here but not in this file',
  'import.straysHint':
    'These are NOT touched by the import — an import that quietly retired rows it did not recognise would be overwriting by another name. Decide on each yourself.',
  'import.strayLocal': '{count} item(s) created here, which never came from an export.',
  'import.seeItems': 'See the items',
  'nav.deadlines': 'Deadlines',
  'nav.reservations': 'Reservations',
  // ⚠️ HYPHENATED ON PURPOSE. "Check outs" contains "Check out", the accessible name of the
  // action button on the item screen — and Playwright's getByRole name option matches a
  // SUBSTRING, so a nav tab called "Check outs" made every `getByRole('button', { name:
  // 'Check out' })` resolve to two elements and fail strict mode. Twenty e2e tests went red
  // on it. The hyphen is not cosmetic: it is what keeps the tab's name from containing the
  // button's. Renaming either one back re-breaks the other.
  'nav.checkouts': 'Check-outs',
  /** ⚠️ NAMES BOTH PANELS. Since 0.1.87 this one tab holds Returns AND Deadlines — they
   * answered a single question ("what is out, and when is it owed back") from two places,
   * so checking one, not finding it, and checking the other was the whole workflow. The
   * label said only half of that until 1 Aug 2026. It sits in the More panel rather than
   * the phone tab bar, so the length costs nothing. */
  'nav.returns': 'Returns & deadlines',
  'nav.reports': 'Reports',
  'nav.maintenance': 'Maintenance',
  'nav.outfits': 'Outfits',
  'nav.projects': 'Projects',
  'nav.racks': 'Rack location',
  'nav.scan': 'Scan',
  'nav.users': 'Users',
  'nav.settings': 'Settings',
  'nav.addItems': '+ Add items',
  'nav.filter': 'Filter',
  'nav.select': 'Select',
  'nav.cancelSelection': 'Cancel selection',

  'common.cancel': 'Cancel',
  'common.save': 'Save',
  'common.apply': 'Apply',
  'common.back': 'Back',
  'common.close': 'Close',
  'common.remove': 'Remove',
  'common.delete': 'Delete',
  'outfits.toNewReservation': 'Add to a new reservation',
  'outfits.toExistingReservation': 'Add to an existing reservation',
  'common.edit': 'Edit',
  'common.loading': 'Loading…',
  'common.nothingMatches': 'Nothing matches',
  'common.none': 'none',
  'common.yes': 'yes',
  'common.search': 'Search items',

  // Item status, which appears on nearly every screen.
  'status.available': 'available',
  'status.checked_out': 'checked out',
  'status.flagged': 'flagged',
  'status.retired': 'retired',
  'status.in_custody': 'in custody',

  // Item detail.
  'item.brand': 'Brand',
  'item.model': 'Model',
  'item.status': 'Status',
  'item.category': 'Category',
  'item.location': 'Location',
  'item.quantity': 'Quantity',
  'item.purchasePrice': 'Purchase price',
  'item.purchased': 'Purchased',
  'item.residualValue': 'Residual value',
  'item.returnDue': 'Return due',
  'item.codes': 'Codes',
  'item.inCatalogueSince': 'In catalogue since',
  'item.panelInfo': 'Info',
  'item.history': 'History',
  'item.addNote': 'Add a note',
  'item.historyImmutable': 'Nothing here can be edited or deleted — corrections are added as new entries.',
  'item.nothingHappenedYet': 'Nothing has happened to this item yet.',
  'item.checkOut': 'Check out',
  'item.checkIn': 'Check in',
  'item.availableLabel': 'Available.',
  'item.checkedOutLabel': 'Checked out.',
  'item.flaggedLabel': 'Flagged — needs attention.',
  'item.flaggedHint': 'Resolve its condition flags below to release it.',
  'item.retire': 'Retire item',
  'item.retireHint': 'Hides it from the list and intake. Nothing is deleted; you can restore it.',
  'item.restore': 'Restore to available',
  'item.retiredBanner': 'This item is retired — hidden from the list and from intake.',
  'item.deleteItem': 'Delete item…',

  'item.cheqroomId': 'Cheqroom id',
  'item.loadingHistory': 'Loading history…',
  'item.checkOutPrompt': 'Check out — where is it going? (optional)',
  'item.checkInPrompt': 'Check in — note (optional)',
  'item.addNotePrompt': 'Add a note to the history',
  'item.saving': 'Saving…',
  'item.removing': 'Removing…',
  'item.description': 'Description',
  'item.photos': 'Photos',
  'item.addPhotos': 'Add photos',
  'item.custodyLabel': 'In custody of {name} — no return date, they simply have it.',
  'item.giveCustody': 'Give into custody…',
  'item.releaseCustody': 'Release custody',
  'item.custodyHint':
    'Ending custody just makes it available again — nothing is lost, and you can hand it straight back.',
  'item.custodyWho': 'Who has it?',
  'item.custodyGive': 'Give',

  // Intake.
  'intake.heading': 'Add items',
  'intake.name': 'Name',
  'intake.saveAndAnother': 'Save & add another',
  'intake.saving': 'Saving…',

  // Condition flags.
  'flags.heading': 'Condition',
  'flags.resolvePrompt': 'How was it resolved? (optional)',
  'flags.none': 'No open flags.',
  'flags.raise': 'Flag a problem…',
  'flags.raiseHint': 'A reason, and a condition photo if it helps. Flagged items cannot be checked out.',
  'flags.whatIsWrong': 'What is wrong with it?',
  'flags.submit': 'Raise flag',
  'flags.resolve': 'Resolve…',
  'flags.photo': 'photo',
  'flags.nonBlocking': 'does not block use',

  // Scanning.
  'scan.heading': 'Scan',
  'scan.start': 'Scan a tag',
  'scan.stop': 'Stop camera',
  'scan.next': 'Scan the next one',
  'scan.openItem': 'Open item',
  'scan.orType': 'Or type a code',
  'scan.code': 'Code',
  'scan.find': 'Find',
  'scan.noCamera': 'This browser gives the app no camera. Type a code below instead.',
  'scan.fallbackNotice': 'This device needs the barcode reader — it downloads once, on the first scan.',
  'scan.doNotSend': 'do not send this out',

  // Reports and maintenance.
  'reports.heading': 'Reports',
  'reports.intro': 'The lists to print and carry. Every report exports as a CSV that opens in Excel.',
  'reports.download': 'Download CSV',
  'reports.empty': 'Nothing to report — this list is empty right now.',
  'reports.running': 'Running…',
  'maintenance.heading': 'Maintenance',
  'maintenance.empty': 'Nothing needs work right now.',
  'maintenance.markDone': 'Mark repaired',
  /**
   * ⚠️ THIS WAS THE SENTENCE THAT CAUSED THE CONFUSION (0.1.405). It read *"cleared in
   * Cheqroom"* — written as an INSTRUCTION ("you clear this one over there, not here"), but it
   * sits in the slot every other row uses for PROVENANCE ("Flagged by Ana · 12 Aug"), so it
   * parsed as a STATUS CLAIM. Reported exactly that way: *"a lot of items ... say they are
   * cleared in Cheqroom, so I don't get why they are still in the maintenance tab"*.
   *
   * It is provenance now, and only provenance — the instruction it used to carry was wrong on
   * its own terms anyway, since Cheqroom was switched off on 7 Aug 2026.
   */
  'maintenance.fromCheqroom': 'From Cheqroom',

  // Bookings.
  'bookings.heading': 'Reservations',
  'bookings.title': 'Title',
  'bookings.client': 'Clients',
  'bookings.noClient': 'No client (internal)',
  'bookings.firstDay': 'First day',
  'bookings.lastDay': 'Last day',
  'bookings.create': 'Create reservation',
  'bookings.none': 'No reservations yet.',
  /* The check-outs empty state (0.1.213, handoff copy, verbatim — the "fix regardless"
     bug: this screen rendered the reservations line above). English in both dictionaries
     per the Dutch hold. */
  'checkouts.none': 'Nothing is out.',
  'checkouts.noneDetail':
    'Every garment is in the building. Check-outs appear here the moment something leaves, with the date it is due back.',
  'bookings.calendar': 'Calendar',
  'bookings.hideCalendar': 'Hide calendar',
  'bookings.clients': 'Clients',
  'bookings.newClient': 'New client…',
  'bookings.addClient': 'Add client',
  'bookings.noClients': 'No clients yet.',
  'bookings.outfits': 'Outfits',
  'bookings.noOutfits': 'No outfits yet.',
  'bookings.items': 'Items',
  'bookings.clashes': '⚠ Clashes',
  'bookings.sourceDeadlines': '⚠ Source deadlines',
  'bookings.addOutfit': 'Add an outfit…',
  'bookings.history': 'History',
  'bookings.email': 'Email',
  'bookings.phone': 'Phone',
  'bookings.company': 'Company',
  'bookings.name': 'Name',
  'bookings.startsAt': 'Starts',
  'bookings.endsAt': 'Ends',
  'bookings.useTimes': 'Use specific hour…',
  'bookings.useWholeDays': 'Use whole days…',
  'bookings.handoutLate': '⚠ Should have gone out {days} day(s) ago — nobody collected it. Hand it over (Out) or cancel it.',

  // Returns.
  'returns.heading': 'Returns',
  'returns.thePass': 'The pass',
  'returns.passHint': 'Everything on loan that is back in the building. Tap an item to pack it for its lender.',
  'returns.nothingOnLoan': 'Nothing is on loan — nothing to return.',
  'returns.pack': 'Pack for return',
  'returns.packed': 'packed ✓',
  'returns.takeOut': 'Take out',
  'returns.markSent': 'Mark sent…',
  'returns.needsPhoto': 'Needs a photo of the parcel.',
  'returns.noParcels': 'No parcels are being packed.',
  'returns.sentOn': 'Sent on',
  'returns.confirmSent': 'Confirm sent',
  'returns.sending': 'Sending…',

  // Deadlines / sources.
  'deadlines.heading': 'Deadlines',
  'deadlines.sources': 'Sources',
  'deadlines.addSource': 'Add source',
  'deadlines.noSources': 'No sources yet.',
  'deadlines.kind': 'Kind',
  'deadlines.choose': 'Choose…',
  'deadlines.contact': 'Contact',
  'deadlines.reference': 'Reference',
  'deadlines.receivedOn': 'Received on',
  'deadlines.dueBackOn': 'Due back on',
  'deadlines.conditions': 'Conditions',
  'deadlines.recordOrder': 'Record order',
  'deadlines.overdue': 'Overdue',
  'deadlines.within7': 'Due within 7 days',
  'deadlines.within14': 'Due in 8–14 days',
  'deadlines.later': 'Due later',
  'deadlines.chasing': 'Clients to chase',
  'deadlines.markChased': 'Mark chased',
  'deadlines.toHandOut': 'To hand out',
  'deadlines.startsToday': 'starts today',
  'deadlines.waitingDays': 'waiting {days} day(s)',

  // Dashboard (the landing screen).
  'dashboard.heading': 'Dashboard',
  'dashboard.itemsByStatus': 'The catalogue',
  'dashboard.needsAction': 'Needs action',
  'dashboard.recentActivity': 'Recent activity',
  'dashboard.recentActivityHint': 'last 30 days',
  'dashboard.recentActivityEmpty': 'Nothing in the last 30 days.',
  /** The actor is unknown: never a blank, which reads as a rendering fault. */
  'dashboard.someone': 'Someone',
  'dashboard.loadMore': 'Load more',
  'dashboard.loadingMore': 'Loading…',
  'dashboard.overdueReservations': 'Overdue reservations',
  'dashboard.lenderReturnsWeek': 'Lender returns due within 7 days',
  'dashboard.openRepairs': 'Open repairs',
  'dashboard.garments': 'garments',
  'dashboard.nothingToday': 'nothing today',
  'dashboard.today': 'Today',
  'dashboard.yesterday': 'Yesterday',
  'dashboard.ledger.act': 'Act',
  'dashboard.ledger.garment': 'Garment',
  'dashboard.ledger.detail': 'Detail',
  'dashboard.ledger.who': 'Who',
  'dashboard.ledger.when': 'When',

  // Structure.
  'structure.heading': 'Structure',
  'structure.categories': 'Categories',
  'structure.locations': 'Locations',
  'structure.fields': 'Fields',
  'structure.createField': 'Create field',
  'structure.rename': 'Rename…',
  'structure.doneRenaming': 'Done renaming',
  'structure.remove': 'Remove options or fields…',
  'structure.doneRemoving': 'Done removing',

  // Settings.
  'settings.heading': 'Settings',
  'settings.export': 'Export',
  'settings.exportFull': 'Export the full catalogue (CSV)',
  'settings.language': 'Language',
  'settings.languageHint': 'The interface language. Your choice is remembered on this device.',
  'settings.theme': 'Appearance',
  'settings.themeHint': '“System” follows this device’s light/dark setting.',
  'theme.system': 'System',
  'theme.light': 'Light',
  'theme.dark': 'Dark',
  'settings.calendar': 'Calendar',
  'settings.about': 'About',
} as const;

export type TranslationKey = keyof typeof EN;

/**
 * Dutch. Typed as a complete record of the English keys, so omitting one is a
 * compile error rather than an English word appearing mid-sentence for a Dutch user.
 */
export const NL: Record<TranslationKey, string> = {
  'nav.items': '← Items',
  'nav.boxes': 'Dozen',
  'nav.retour': 'Retour',
  'nav.itemsTab': 'Items',
  'nav.more': 'Meer',
  'nav.groupGoods': 'The goods',
  'nav.groupDesk': 'The desk',
  'nav.closeMenu': 'Menu sluiten',
  'nav.dashboard': 'Dashboard',
  'nav.import': 'Importeren',

  // Importscherm.
  'import.heading': 'Catalogus importeren',
  'import.intro':
    'Leest een CSV-export uit een ander systeem en VOEGT TOE wat ontbreekt. Alleen voor beheerders.',
  'import.file': 'Het CSV-bestand',
  'import.check': 'Bestand controleren',
  'import.checking': 'Bezig met controleren…',
  'import.checkHint': 'Er wordt nog niets weggeschreven — dit toont wat er zou gebeuren.',
  'import.previewHeading': 'Wat deze import zou doen',
  'import.doneHeading': 'Geïmporteerd',
  'import.rowsRead': 'Rijen gelezen',
  'import.itemsToAdd': 'Toe te voegen items',
  'import.itemsAdded': 'Items toegevoegd',
  'import.skippedExisting': 'Al aanwezig, overgeslagen',
  'import.newCategories': 'Nieuwe categorieën',
  'import.newLocations': 'Nieuwe locaties',
  'import.newFields': 'Nieuwe velden',
  'import.custodyRows': 'Items die in bewaring aankomen',
  'import.photosToLink': 'Gekoppelde foto’s',
  'import.codesSkipped': 'Labelcodes NIET overgenomen',
  'import.codesSkippedHint': 'Een ander item draagt deze labels al, dus die blijven waar ze zijn. Het item wordt wel geïmporteerd.',
  'import.rejects': 'Niet geïmporteerde waarden',
  'import.rejectsHint': 'Elke waarde wordt benoemd in plaats van geraden. De rij komt binnen, de waarde niet.',
  'import.unmapped': 'Kolommen die niets importeren',
  'import.unmappedHint': 'Aanwezig in het bestand en niet gekoppeld door dit profiel. Er verdwijnt hier niets stilzwijgend.',
  'import.row': 'rij',
  'import.rowsWord': 'rijen',
  'import.andMore': '…en nog {count}.',
  'import.confirm': '{count} items importeren',
  'import.importing': 'Bezig met importeren…',
  'import.nothingToAdd': 'Alles in dit bestand staat er al.',
  'import.recognisedAs': 'Herkend als {profile}',
  'import.strays': 'Hier aanwezig, niet in dit bestand',
  'import.straysHint':
    'Deze worden NIET aangeraakt door de import — stilzwijgend afvoeren wat niet herkend wordt, is overschrijven onder een andere naam. Beslis er zelf over.',
  'import.strayLocal': '{count} item(s) hier aangemaakt, die nooit uit een export kwamen.',
  'import.seeItems': 'Bekijk de items',
  'nav.deadlines': 'Deadlines',
  'nav.reservations': 'Reserveringen',
  // MISSING: not judged by a native Dutch reader — the standing open item in
  // screen-design-review §8 applies to this string too. "Uitgifte & retour" names both
  // halves the tab holds (out, and checked back in) and matches the existing 'Retours'
  // vocabulary; a native reader may prefer a single noun.
  'nav.checkouts': 'Uitgifte & retour',
  'nav.returns': 'Retours & deadlines',
  'nav.reports': 'Rapporten',
  'nav.maintenance': 'Onderhoud',
  'nav.outfits': 'Outfits',
  'nav.projects': 'Projects',
  'nav.racks': 'Rack location',
  'nav.scan': 'Scannen',
  'nav.users': 'Gebruikers',
  'nav.settings': 'Instellingen',
  'nav.addItems': '+ Items toevoegen',
  'nav.filter': 'Filter',
  'nav.select': 'Selecteren',
  'nav.cancelSelection': 'Selectie annuleren',

  'common.cancel': 'Annuleren',
  'common.save': 'Opslaan',
  'common.apply': 'Toepassen',
  'common.back': 'Terug',
  'common.close': 'Sluiten',
  'common.remove': 'Verwijderen',
  'common.delete': 'Verwijderen',
  'outfits.toNewReservation': 'Aan nieuwe reservering toevoegen',
  'outfits.toExistingReservation': 'Aan bestaande reservering toevoegen',
  'common.edit': 'Bewerken',
  'common.loading': 'Laden…',
  'common.nothingMatches': 'Niets gevonden',
  'common.none': 'geen',
  'common.yes': 'ja',
  'common.search': 'Items zoeken',

  'status.available': 'beschikbaar',
  'status.checked_out': 'uitgeleend',
  'status.flagged': 'gemarkeerd',
  'status.retired': 'uit roulatie',
  'status.in_custody': 'in bewaring',

  'item.brand': 'Merk',
  'item.model': 'Model',
  'item.status': 'Status',
  'item.category': 'Categorie',
  'item.location': 'Locatie',
  'item.quantity': 'Aantal',
  'item.purchasePrice': 'Aankoopprijs',
  'item.purchased': 'Gekocht op',
  'item.residualValue': 'Restwaarde',
  'item.returnDue': 'Retour vóór',
  'item.codes': 'Codes',
  'item.inCatalogueSince': 'In catalogus sinds',
  'item.panelInfo': 'Info',
  'item.history': 'Geschiedenis',
  'item.addNote': 'Notitie toevoegen',
  'item.historyImmutable':
    'Niets hier kan bewerkt of verwijderd worden — correcties worden als nieuwe regels toegevoegd.',
  'item.nothingHappenedYet': 'Er is nog niets met dit item gebeurd.',
  'item.checkOut': 'Uitlenen',
  'item.checkIn': 'Innemen',
  'item.availableLabel': 'Beschikbaar.',
  'item.checkedOutLabel': 'Uitgeleend.',
  'item.flaggedLabel': 'Gemarkeerd — vraagt aandacht.',
  'item.flaggedHint': 'Los de conditiemeldingen hieronder op om het weer vrij te geven.',
  'item.retire': 'Uit roulatie halen',
  'item.retireHint': 'Verbergt het item in de lijst en bij intake. Er wordt niets verwijderd; je kunt het terugzetten.',
  'item.restore': 'Terugzetten als beschikbaar',
  'item.retiredBanner': 'Dit item is uit roulatie — verborgen in de lijst en bij intake.',
  'item.deleteItem': 'Item verwijderen…',

  'item.cheqroomId': 'Cheqroom-id',
  'item.loadingHistory': 'Geschiedenis laden…',
  'item.checkOutPrompt': 'Uitlenen — waar gaat het heen? (optioneel)',
  'item.checkInPrompt': 'Innemen — notitie (optioneel)',
  'item.addNotePrompt': 'Notitie toevoegen aan de geschiedenis',
  'item.saving': 'Opslaan…',
  'item.removing': 'Verwijderen…',
  'item.description': 'Omschrijving',
  'item.photos': "Foto's",
  'item.addPhotos': "Foto's toevoegen",
  'item.custodyLabel': 'In bewaring bij {name} — geen retourdatum, die persoon heeft het gewoon.',
  'item.giveCustody': 'In bewaring geven…',
  'item.releaseCustody': 'Bewaring beëindigen',
  'item.custodyHint':
    'Bewaring beëindigen maakt het stuk gewoon weer beschikbaar — er gaat niets verloren, en je kunt het meteen opnieuw in bewaring geven.',
  'item.custodyWho': 'Wie heeft het?',
  'item.custodyGive': 'Geven',

  'intake.heading': 'Items toevoegen',
  'intake.name': 'Naam',
  'intake.saveAndAnother': 'Opslaan & nog een toevoegen',
  'intake.saving': 'Opslaan…',

  'flags.heading': 'Conditie',
  'flags.resolvePrompt': 'Hoe is het opgelost? (optioneel)',
  'flags.none': 'Geen open meldingen.',
  'flags.raise': 'Probleem melden…',
  'flags.raiseHint': 'Een reden, en een conditiefoto als dat helpt. Gemarkeerde items kunnen niet uitgeleend worden.',
  'flags.whatIsWrong': 'Wat is er mis mee?',
  'flags.submit': 'Melding maken',
  'flags.resolve': 'Oplossen…',
  'flags.photo': 'foto',
  'flags.nonBlocking': 'blokkeert gebruik niet',

  'scan.heading': 'Scannen',
  'scan.start': 'Label scannen',
  'scan.stop': 'Camera stoppen',
  'scan.next': 'Volgende scannen',
  'scan.openItem': 'Item openen',
  'scan.orType': 'Of typ een code',
  'scan.code': 'Code',
  'scan.find': 'Zoeken',
  'scan.noCamera': 'Deze browser geeft de app geen camera. Typ hieronder een code.',
  'scan.fallbackNotice': 'Dit toestel heeft de barcodelezer nodig — die wordt één keer gedownload, bij de eerste scan.',
  'scan.doNotSend': 'niet meegeven',

  'reports.heading': 'Rapporten',
  'reports.intro': 'De lijsten om te printen en mee te nemen. Elk rapport downloadt als CSV die in Excel opent.',
  'reports.download': 'CSV downloaden',
  'reports.empty': 'Niets te melden — deze lijst is nu leeg.',
  'reports.running': 'Bezig…',
  'maintenance.heading': 'Onderhoud',
  'maintenance.empty': 'Er is nu niets te doen.',
  'maintenance.markDone': 'Afgehandeld',
  // Corrected alongside the English (0.1.405) — the old Dutch said "tick off in Cheqroom",
  // which is the same wrong instruction. Not a NEW translation; an existing one made true.
  'maintenance.fromCheqroom': 'Uit Cheqroom',

  'bookings.heading': 'Boekingen',
  'bookings.title': 'Titel',
  'bookings.client': 'Klant',
  'bookings.noClient': 'Geen klant (intern)',
  'bookings.firstDay': 'Eerste dag',
  'bookings.lastDay': 'Laatste dag',
  'bookings.create': 'Boeking aanmaken',
  'bookings.none': 'Nog geen boekingen.',
  'checkouts.none': 'Nothing is out.',
  'checkouts.noneDetail':
    'Every garment is in the building. Check-outs appear here the moment something leaves, with the date it is due back.',
  'bookings.calendar': 'Kalender',
  'bookings.hideCalendar': 'Kalender verbergen',
  'bookings.clients': 'Klanten',
  'bookings.newClient': 'Nieuwe klant…',
  'bookings.addClient': 'Klant toevoegen',
  'bookings.noClients': 'Nog geen klanten.',
  'bookings.outfits': 'Outfits',
  'bookings.noOutfits': 'Nog geen outfits.',
  'bookings.items': 'Items',
  'bookings.clashes': '⚠ Conflicten',
  'bookings.sourceDeadlines': '⚠ Deadlines van uitleners',
  'bookings.addOutfit': 'Outfit toevoegen…',
  'bookings.history': 'Geschiedenis',
  'bookings.email': 'E-mail',
  'bookings.phone': 'Telefoon',
  'bookings.company': 'Bedrijf',
  'bookings.name': 'Naam',
  'bookings.startsAt': 'Start',
  'bookings.endsAt': 'Einde',
  'bookings.useTimes': 'Met tijden…',
  'bookings.useWholeDays': 'Hele dagen…',
  'bookings.handoutLate': '⚠ Had {days} dag(en) geleden al mee moeten gaan — niemand heeft het opgehaald. Geef het mee (Out) of annuleer.',

  'returns.heading': 'Retours',
  'returns.thePass': 'De ronde',
  'returns.passHint': 'Alles wat geleend is en terug in huis is. Tik een item aan om het in te pakken voor de uitlener.',
  'returns.nothingOnLoan': 'Er is niets geleend — niets om terug te sturen.',
  'returns.pack': 'Inpakken voor retour',
  'returns.packed': 'ingepakt ✓',
  'returns.takeOut': 'Eruit halen',
  'returns.markSent': 'Markeren als verstuurd…',
  'returns.needsPhoto': 'Heeft een foto van het pakket nodig.',
  'returns.noParcels': 'Er wordt geen pakket ingepakt.',
  'returns.sentOn': 'Verstuurd op',
  'returns.confirmSent': 'Verzending bevestigen',
  'returns.sending': 'Versturen…',

  'deadlines.heading': 'Deadlines',
  'deadlines.sources': 'Uitleners',
  'deadlines.addSource': 'Uitlener toevoegen',
  'deadlines.noSources': 'Nog geen uitleners.',
  'deadlines.kind': 'Soort',
  'deadlines.choose': 'Kiezen…',
  'deadlines.contact': 'Contact',
  'deadlines.reference': 'Referentie',
  'deadlines.receivedOn': 'Ontvangen op',
  'deadlines.dueBackOn': 'Terug vóór',
  'deadlines.conditions': 'Voorwaarden',
  'deadlines.recordOrder': 'Levering vastleggen',
  'deadlines.overdue': 'Te laat',
  'deadlines.within7': 'Binnen 7 dagen terug',
  'deadlines.within14': 'Over 8–14 dagen terug',
  'deadlines.later': 'Later terug',
  'deadlines.chasing': 'Klanten om aan te manen',
  'deadlines.markChased': 'Aangemaand',
  'deadlines.toHandOut': 'Klaar om mee te geven',
  'deadlines.startsToday': 'start vandaag',
  'deadlines.waitingDays': 'wacht al {days} dag(en)',

  // Dashboard.
  'dashboard.heading': 'Dashboard',
  'dashboard.itemsByStatus': 'Items per status',
  'dashboard.needsAction': 'Actie nodig',
  'dashboard.recentActivity': 'Recente activiteit',
  'dashboard.recentActivityHint': 'last 30 days',
  'dashboard.recentActivityEmpty': 'Niets in de laatste 30 dagen.',
  'dashboard.someone': 'Iemand',
  // Dutch is on hold (CLAUDE.md): new strings ship in English in both dictionaries.
  'dashboard.loadMore': 'Load more',
  'dashboard.loadingMore': 'Loading…',
  'dashboard.overdueReservations': 'Overdue reservations',
  'dashboard.lenderReturnsWeek': 'Lender returns due within 7 days',
  'dashboard.openRepairs': 'Open repairs',
  'dashboard.garments': 'garments',
  'dashboard.nothingToday': 'nothing today',
  'dashboard.today': 'Today',
  'dashboard.yesterday': 'Yesterday',
  'dashboard.ledger.act': 'Act',
  'dashboard.ledger.garment': 'Garment',
  'dashboard.ledger.detail': 'Detail',
  'dashboard.ledger.who': 'Who',
  'dashboard.ledger.when': 'When',

  'structure.heading': 'Structuur',
  'structure.categories': 'Categorieën',
  'structure.locations': 'Locaties',
  'structure.fields': 'Velden',
  'structure.createField': 'Veld aanmaken',
  'structure.rename': 'Hernoemen…',
  'structure.doneRenaming': 'Klaar met hernoemen',
  'structure.remove': 'Opties of velden verwijderen…',
  'structure.doneRemoving': 'Klaar met verwijderen',

  'settings.heading': 'Instellingen',
  'settings.export': 'Export',
  'settings.exportFull': 'Volledige catalogus exporteren (CSV)',
  'settings.language': 'Taal',
  'settings.languageHint': 'De taal van de interface. Je keuze wordt op dit toestel onthouden.',
  'settings.theme': 'Weergave',
  'settings.themeHint': '“Systeem” volgt de licht/donker-instelling van dit toestel.',
  'theme.system': 'Systeem',
  'theme.light': 'Licht',
  'theme.dark': 'Donker',
  'settings.calendar': 'Agenda',
  'settings.about': 'Over',
};

const DICTIONARIES: Record<Language, Record<TranslationKey, string>> = { en: EN, nl: NL };

const STORAGE_KEY = 'mononoke.language';

function isLanguage(value: unknown): value is Language {
  return value === 'nl' || value === 'en';
}

/**
 * The stored choice, else **English**.
 *
 * CHANGED 30 Jul 2026 (goods-flow spec D-GF10). This used to auto-select Dutch from
 * `navigator.language`, on the reasoning that four of the five users are Dutch
 * speakers. The atelier watched their own app in Dutch on a phone and asked for the
 * opposite: *"dat mag trouwens volledig in het Engels voor ons hoor"* / *"ik zou
 * gewoon eerst vol Engels doen"*. English is the standard they want.
 *
 * The browser sniff is GONE rather than reordered, because a phone set to Dutch is
 * exactly the case that produced the unwanted Dutch UI — leaving the sniff in place
 * behind English would change nothing for the people who complained.
 *
 * Dutch is NOT removed: the dictionary, the switcher and `docs/admin-guide-nl-v1_0.md`
 * all stay, so a user who wants Dutch chooses it once and `localStorage` remembers.
 * A stored choice still wins, which is what makes that true.
 */
export function detectLanguage(): Language {
  const stored = readSetting(STORAGE_KEY);
  if (isLanguage(stored)) return stored;
  return 'en';
}

let current: Language = 'en';
const listeners = new Set<(language: Language) => void>();

export function language(): Language {
  return current;
}

export function setLanguage(next: Language): void {
  current = next;
  writeSetting(STORAGE_KEY, next);
  for (const listener of listeners) listener(next);
}

/** Subscribe to language changes; fires immediately with the current one, like
 * subscribeRoute. Returns the unsubscribe. */
export function subscribeLanguage(listener: (language: Language) => void): () => void {
  listeners.add(listener);
  listener(current);
  return () => listeners.delete(listener);
}

/** Call once at boot, before the first render. */
export function initLanguage(): Language {
  current = detectLanguage();
  return current;
}

/**
 * Translate. `params` interpolates `{name}` placeholders — deliberately the only
 * feature beyond lookup, because everything else this app needs (counts, dates) is
 * already formatted by the caller.
 */
export function t(key: TranslationKey, params?: Record<string, string | number>): string {
  const template = DICTIONARIES[current][key];
  if (params === undefined) return template;
  return template.replace(/\{(\w+)\}/g, (whole, name: string) =>
    Object.prototype.hasOwnProperty.call(params, name) ? String(params[name]) : whole,
  );
}

/** Item status, translated — it is rendered on six screens and stored in English. */
export function tStatus(status: string): string {
  switch (status) {
    case 'available':
      return t('status.available');
    case 'checked_out':
      return t('status.checked_out');
    case 'flagged':
      return t('status.flagged');
    case 'retired':
      return t('status.retired');
    case 'in_custody':
      return t('status.in_custody');
    default:
      // An unknown status is shown RAW rather than hidden: a new enum value that
      // nobody translated should look untranslated, not disappear.
      return status;
  }
}
